import React, { FC, useEffect, useRef, useState } from 'react';
import { Moment } from 'moment';
import TimezoneDatePicker from 'components/DatePicker/TimezoneDatePicker';
import { Input } from 'components/Input';
import { Icon, TIconNames } from 'components/Icon';
import { Colors } from 'core/CssVariables';
import { getDateTimeFormats } from 'helpers/utils';
import {
  formatTimeOfDay,
  minsOfDay,
  parseTimeInput,
  TViewMode,
  withTimeOfDay,
} from './timeRange.utils';
import { SDateTimeRow, SPeriodBtn, SPeriodToggle } from './DateTimeInput.style';

type TPeriod = 'AM' | 'PM';

type Props = {
  /** Injected by AntD Form.Item — the current datetime value. */
  value?: Moment | null;
  /** Injected by AntD Form.Item — fires with the merged datetime. */
  onChange?: (value: Moment | null) => void;
  viewMode: TViewMode;
  id?: string;
  /** Greys out calendar dates, e.g. the past for a scheduling reminder. */
  disabledDate?: (current: Moment) => boolean;
};

/** 12-hour clock text (no meridiem), e.g. 14:05 -> "2:05". */
const formatClock = (mins: number): string => {
  const m = ((mins % 1440) + 1440) % 1440;
  const min = m % 60;
  const h24 = Math.floor(m / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(min).padStart(2, '0')}`;
};

const periodOf = (value: Moment): TPeriod => (value.hour() < 12 ? 'AM' : 'PM');

/**
 * ZEST-692: Replaces the hour/minute/AM-PM dropdown + OK picker with a
 * date-only calendar plus a free-typed time field. The time can be typed
 * directly (e.g. "2:05" or "1405"); in 12-hour mode an AM/PM switch flips
 * the meridiem with a single click instead of typing letters. Enter submits.
 */
const DateTimeInput: FC<Props> = ({
  value,
  onChange,
  viewMode,
  id,
  disabledDate,
}) => {
  const { dateFormat } = getDateTimeFormats();
  const is12h = viewMode === '12';
  const [text, setText] = useState('');
  const focusedRef = useRef(false);

  const displayText = (v: Moment): string =>
    is12h ? formatClock(minsOfDay(v)) : formatTimeOfDay(minsOfDay(v), '24');

  // Keep the visible text in sync with the value / view mode while not editing.
  useEffect(() => {
    if (!focusedRef.current) {
      setText(value ? displayText(value) : '');
    }
  }, [value, viewMode]);

  const handleDateChange = (date: Moment | null) => {
    if (!date) {
      if (onChange) onChange(null);
      return;
    }
    // Preserve the existing time-of-day, only swap the calendar date.
    const merged = value
      ? date
          .clone()
          .hour(value.hour())
          .minute(value.minute())
          .second(0)
          .millisecond(0)
      : date;
    if (onChange) onChange(merged);
  };

  // Parse the typed text into minutes-of-day. In 12-hour mode, when the user
  // omits AM/PM we keep the current meridiem instead of forcing morning.
  const parseWithPeriod = (raw: string): number | null => {
    const base = parseTimeInput(raw);
    if (base === null) return null;
    if (!is12h) return base;
    const hasMeridiem = /[ap]\.?m\.?/i.test(raw);
    if (hasMeridiem) return base;
    // An unambiguous 24h hour (13–23) is taken as-is rather than folded
    // through the 12h clock, so e.g. "14:05" stays 2:05 PM.
    if (Math.floor(base / 60) > 12) return base;
    const period: TPeriod = value ? periodOf(value) : 'AM';
    const clockHour = Math.floor(base / 60) % 12;
    const min = base % 60;
    const hour24 = period === 'PM' ? clockHour + 12 : clockHour;
    return hour24 * 60 + min;
  };

  const commitText = () => {
    focusedRef.current = false;
    const mins = parseWithPeriod(text);
    if (mins === null || !value) {
      setText(value ? displayText(value) : '');
      return;
    }
    const next = withTimeOfDay(value, mins);
    setText(displayText(next));
    if (onChange) onChange(next);
  };

  const setPeriod = (period: TPeriod) => {
    if (!value || periodOf(value) === period) return;
    const shifted =
      period === 'PM'
        ? withTimeOfDay(value, minsOfDay(value) + 12 * 60)
        : withTimeOfDay(value, minsOfDay(value) - 12 * 60);
    if (onChange) onChange(shifted);
  };

  const currentPeriod: TPeriod = value ? periodOf(value) : 'AM';

  return (
    <SDateTimeRow>
      <div className="zest-datetime-date">
        <TimezoneDatePicker
          id={id}
          value={value}
          format={dateFormat}
          allowClear={false}
          disabledDate={disabledDate}
          onChange={handleDateChange}
        />
      </div>
      <div className="zest-datetime-timewrap">
        <Input
          className="zest-datetime-time"
          value={text}
          placeholder={is12h ? 'h:mm' : 'HH:mm'}
          aria-label="Time"
          suffix={
            <Icon icon={TIconNames.CLOCK} size={14} color={Colors.Grey} />
          }
          onFocus={() => {
            focusedRef.current = true;
          }}
          onChange={e => setText(e.target.value)}
          onBlur={commitText}
          // Commit the typed value; the form-level Enter handler then submits.
          onPressEnter={commitText}
        />
        {is12h && (
          <SPeriodToggle>
            <SPeriodBtn
              type="button"
              active={currentPeriod === 'AM'}
              onClick={() => setPeriod('AM')}
            >
              AM
            </SPeriodBtn>
            <SPeriodBtn
              type="button"
              active={currentPeriod === 'PM'}
              onClick={() => setPeriod('PM')}
            >
              PM
            </SPeriodBtn>
          </SPeriodToggle>
        )}
      </div>
    </SDateTimeRow>
  );
};

export default DateTimeInput;
